import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Minimal React error boundary so runtime errors render a stable fallback
 * instead of a blank page. Referenced by `main.tsx` and `layouts/AppShell`;
 * this file was missing from the develop branch, blocking builds.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-6">
          <div className="max-w-md w-full glass-card rounded-2xl p-6 text-center">
            <h1 className="text-title font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-meta text-gray-500 dark:text-gray-400 mb-4">
              An unexpected error occurred. Reloading the page usually fixes it.
            </p>
            {this.state.error?.message && (
              <pre className="text-micro text-left bg-gray-100 dark:bg-white/5 rounded-lg p-3 overflow-auto max-h-40 mb-4 text-gray-700 dark:text-gray-300">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={this.handleReload}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-white text-meta font-medium hover:bg-primary/90 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
